
import { reducerFor } from "./EvalCompiled.js";
import { fastReduce } from "./EvalReference.js";
import { isInput, isCustom } from "../../Common/Helpers.js";
import { equals } from "../../fable_modules/fable-library-js.5.13.0/Util.js";
import { head, isEmpty, ofArray, map, collect, filter, empty } from "../../fable_modules/fable-library-js.5.13.0/List.js";
import { couldBeSynchronousComponent } from "../SynchronousUtils.js";
import { WaveTables, FastSimulation } from "../SimTypes.js";
import { enabled } from "../../Shared/Log.js";
import { usedHeapBytes, getTimeMs } from "../../Common/TimeHelpers.js";
import { checkSimulationFits, linkCustomComponentPorts, addWavesToFastSimulation, emptyFastSimulation, createInitFastCompPhase, linkFastComponents, determineBigIntState, gatherSimulation, finishStepArena, startStepArena, costAndSizeOfGraph } from "./FastCreate.js";
import { FSharpResult$2, Result_Map, Result_Bind } from "../../fable_modules/fable-library-js.5.13.0/Result.js";
import { toArray } from "../../Common/LookupArray.js";
import { checkAndValidateFData, checkAndValidate } from "./FastValidate.js";
import { orderCombinationalComponentsFData, orderCombinationalComponents } from "./FastOrder.js";
import { item } from "../../fable_modules/fable-library-js.5.13.0/Array.js";
import { skip, fold } from "../../fable_modules/fable-library-js.5.13.0/Seq.js";
import { dbg } from "../../Shared/Log.js";
import { printf, toText, interpolate } from "../../fable_modules/fable-library-js.5.13.0/String.js";
import { Constants_maxTruthTableBusWidth } from "../TruthTableTypes.js";
import { SimulationError, SimulationErrorType } from "../SimGraphTypes.js";

/**
 * Bind each simulated component's reducer to the component itself, once, when the simulation
 * is built. The loop then calls a component's own code rather than dispatching on FType for
 * every component of every step. EvalCompiled.reducerFor returns None for a type it does not
 * handle yet, and that component keeps the general fastReduce.
 * 
 * This must run last. The reducers capture the step arrays their ports currently point at, and
 * linkFastComponents, reLinkIOLabels and addWavesToFastSimulation all re-point ports; a reducer
 * installed before them could hold an array the simulation no longer uses. It is also why only
 * the components that are actually reduced get one - the custom-component FastComponents that
 * addWavesToFastSimulation re-points exist for waveform display and are never reduced.
 */
export function installReducers(fs) {
    const install = (fc) => {
        const matchValue = reducerFor(fc);
        if (matchValue == null) {
            fc.ReduceComb = ((step) => {
                fastReduce(step, false, fc);
            });
            fc.ReduceClocked = ((step_1) => {
                fastReduce(step_1, true, fc);
            });
        }
        else {
            const reduce = matchValue;
            fc.ReduceComb = reduce;
            fc.ReduceClocked = reduce;
        }
    };
    fs.FClockedComps.forEach(install);
    fs.FOrderedComps.forEach(install);
    return fs;
}

/**
 * The three arrays the run loop and the ordering pass iterate, taken from `comps` - every
 * component of the build, in the order the gather created them.
 * 
 * Not just to avoid building three throwaway Maps to filter with, though it does that. The
 * components are ALLOCATED in gather order, so walking them in that order walks memory forwards;
 * walking them through a map keyed by (ComponentId, access path) - which is what a built
 * simulation offered when this was written - visits the same objects in an order unrelated to
 * where they sit. On a 15,000-component design that difference was worth more
 * than everything else in this phase put together - and it applies to every pass that walks all
 * the components, which is why determineBigIntState and addWavesToFastSimulation take the array
 * too.
 */
export function createFastArrays(comps, fs) {
    const getArrayOf = (pred) => comps.filter((fc) => {
        if (!isCustom(fc.FType)) {
            return pred(fc);
        }
        else {
            return false;
        }
    });
    const FGlobalInputComps = getArrayOf((fc_1) => (isInput(fc_1.FType) && equals(fc_1.AccessPath, empty())));
    const FConstantComps = getArrayOf((fc_2) => (fc_2.FType.tag === 7));
    const FClockedComps = getArrayOf((fc_3) => couldBeSynchronousComponent(fc_3.FType));
    return new FastSimulation(fs.ClockTick, fs.MaxArraySize, FGlobalInputComps, FConstantComps, FClockedComps, new Array(0), fs.FIOActive, fs.FIOLinks, fs.FCompsByIndex, fs.FIndexOf, fs.FCustomOutputCompLookup, fs.NumStepArrays, fs.Drivers, fs.WaveIndex, fs.Design, fs.StepCost);
}

/**
 * Create a fast simulation data structure, with all necessary arrays, and components
 * ordered for evaluation.
 * This function also creates the reducer functions for each component
 * similar to the reducer builder in Builder, but with inputs and outputs using the FastSimulation
 * mutable arrays
 * 
 * `waveTables` says whether to build the structures only a wave VIEWER reads - see WaveTables.
 * Everything else about the build is the same either way, deliberately: the two simulators must
 * run identical code over identical structures, or they agree until they do not.
 */
export function buildFastSimulationWith(waveTables, simulationArraySize, diagramName, graph) {
    const marks = [];
    const mark = (name, x) => {
        if ((enabled() & 512) !== 0) {
            void (marks.push([name, getTimeMs(), usedHeapBytes()]));
        }
        return x;
    };
    mark("start", undefined);
    const patternInput_1 = costAndSizeOfGraph(graph);
    const cost = patternInput_1[0];
    const result = Result_Bind(() => {
        startStepArena(cost, simulationArraySize);
        try {
            const gather = mark("gather", gatherSimulation(simulationArraySize, patternInput_1[1], graph));
            const comps = toArray(gather.Comps);
            const fs = mark("bigIntState", determineBigIntState(comps, mark("link", linkFastComponents(gather, mark("createInit", createInitFastCompPhase(simulationArraySize, gather, emptyFastSimulation(diagramName)))))));
            return Result_Map((fs_6) => (new FastSimulation(fs_6.ClockTick, fs_6.MaxArraySize, fs_6.FGlobalInputComps, fs_6.FConstantComps, fs_6.FClockedComps, fs_6.FOrderedComps, fs_6.FIOActive, fs_6.FIOLinks, fs_6.FCompsByIndex, fs_6.FIndexOf, fs_6.FCustomOutputCompLookup, fs_6.NumStepArrays, fs_6.Drivers, fs_6.WaveIndex, fs_6.Design, cost)), mark("reducers", Result_Map(installReducers, mark("waves", Result_Map((fs_4) => {
                if (waveTables.tag === 1) {
                    return fs_4;
                }
                else {
                    return addWavesToFastSimulation(comps, fs_4);
                }
            }, Result_Map((fs_3) => linkCustomComponentPorts(comps, fs_3), mark("validate", checkAndValidate(mark("order", orderCombinationalComponents(simulationArraySize, mark("arrays", createFastArrays(comps, fs))))))))))));
        }
        finally {
            finishStepArena();
        }
    }, checkSimulationFits(simulationArraySize, cost));
    if (((enabled() & 512) !== 0) && (marks.length > 1)) {
        const patternInput = item(0, marks);
        const t0 = patternInput[1];
        const m0 = patternInput[2];
        const tupledArg_2 = fold((tupledArg, tupledArg_1) => {
            const name_1 = tupledArg_1[0];
            const t = tupledArg_1[1];
            const m = tupledArg_1[2];
            dbg(512, `build ${interpolate("%-12s%P()", [name_1])} ${interpolate("%8.0f%P()", [t - tupledArg[1]])}ms  ${interpolate("%+6.0f%P()", [(m - tupledArg[2]) / 1000000])}MB`);
            return [name_1, t, m];
        }, [patternInput[0], t0, m0], skip(1, marks));
        dbg(512, `build ${interpolate("%-12s%P()", [diagramName])} ${interpolate("%8.0f%P()", [tupledArg_2[1] - t0])}ms  ${interpolate("%+6.0f%P()", [(tupledArg_2[2] - m0) / 1000000])}MB total`);
    }
    return result;
}

/**
 * A build with everything, which is what a simulation interrogated in this process needs.
 */
export function buildFastSimulation(simulationArraySize, diagramName, graph) {
    return buildFastSimulationWith(WaveTables.WithWaveTables, simulationArraySize, diagramName, graph);
}

function checkWidthsForFData(fs) {
    const tooWide = filter((tupledArg) => (tupledArg[1] > Constants_maxTruthTableBusWidth), collect((fc_1) => map((out) => [fc_1, out.Width], ofArray(fc_1.Outputs)), filter((fc) => {
        if (fc.FType.tag === 26) {
            return false;
        }
        else {
            return true;
        }
    }, ofArray(fs.FCompsByIndex))));
    if (!isEmpty(tooWide)) {
        return new FSharpResult$2(/* Error */ 1, [new SimulationError(new SimulationErrorType(/* GenericSimError */ 20, [toText(printf("A truth table is made for combinational logic narrow enough to tabulate, and \'%s\' carries a %d-bit bus - wider than the %d bits this supports. Use the Wave Simulation tab to see a wide design working, or select a narrower part of the sheet for a table."))(head(tooWide)[0].FullName)(head(tooWide)[1])(Constants_maxTruthTableBusWidth)]), undefined, map((tupledArg_1) => tupledArg_1[0].cId, tooWide), empty())]);
    }
    else {
        return new FSharpResult$2(/* Ok */ 0, [fs]);
    }
}

export function buildFastSimulationFData(simulationArraySize, diagramName, graph) {
    const patternInput = costAndSizeOfGraph(graph);
    const cost = patternInput[0];
    return Result_Bind(() => {
        startStepArena(cost, simulationArraySize);
        try {
            const gather = gatherSimulation(simulationArraySize, patternInput[1], graph);
            const comps = toArray(gather.Comps);
            return Result_Map((fs_5) => (new FastSimulation(fs_5.ClockTick, fs_5.MaxArraySize, fs_5.FGlobalInputComps, fs_5.FConstantComps, fs_5.FClockedComps, fs_5.FOrderedComps, fs_5.FIOActive, fs_5.FIOLinks, fs_5.FCompsByIndex, fs_5.FIndexOf, fs_5.FCustomOutputCompLookup, fs_5.NumStepArrays, fs_5.Drivers, fs_5.WaveIndex, fs_5.Design, cost)), Result_Map((fs_4) => addWavesToFastSimulation(comps, fs_4), Result_Bind(checkAndValidateFData, Result_Map((fs_2) => orderCombinationalComponentsFData(simulationArraySize, fs_2), checkWidthsForFData(createFastArrays(comps, linkFastComponents(gather, createInitFastCompPhase(simulationArraySize, gather, emptyFastSimulation(diagramName)))))))));
        }
        finally {
            finishStepArena();
        }
    }, checkSimulationFits(simulationArraySize, cost));
}

